import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
    BsCheckCircleFill,
    BsFillExclamationCircleFill,
    BsFillExclamationTriangleFill,
    BsInfoCircleFill
} from 'react-icons/bs';
import { toasterRemove } from '../redux/toasterSlice';

const TOAST_DURATION_MS = 4000;
const EXIT_ANIMATION_MS = 300;

const TOAST_STYLES: Record<string, { bgColor: string; icon: React.ReactNode }> = {
    success: { bgColor: '#5cb85c', icon: <BsCheckCircleFill /> },
    error: { bgColor: '#d9534f', icon: <BsFillExclamationCircleFill /> },
    warning: { bgColor: '#f0ad4e', icon: <BsFillExclamationTriangleFill /> },
    info: { bgColor: '#5bc0de', icon: <BsInfoCircleFill /> },
};

const DEFAULT_TOAST_STYLE = TOAST_STYLES.success;

const Alert: React.FC = () => {
    const toaster = useSelector((state: RootState) => state.alert.toaster);
    const dispatch = useDispatch();
    const [exitingIds, setExitingIds] = useState<Set<number>>(new Set());
    const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
    const exitingRef = useRef<Set<number>>(new Set());

    const clearTimer = (id: number) => {
        const timer = timersRef.current.get(id);
        if (timer != null) {
            clearTimeout(timer);
            timersRef.current.delete(id);
        }
    };

    const deleteToast = (id: number) => {
        clearTimer(id);
        exitingRef.current.delete(id);
        setExitingIds(prev => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
        dispatch(toasterRemove(id));
    };

    const startExit = (id: number) => {
        if (exitingRef.current.has(id)) return;
        exitingRef.current.add(id);
        setExitingIds(prev => new Set(prev).add(id));
        clearTimer(id);
        timersRef.current.set(
            id,
            setTimeout(() => deleteToast(id), EXIT_ANIMATION_MS)
        );
    };

    useEffect(() => {
        const activeIds = new Set<number>();

        toaster.forEach((toast) => {
            const id = toast.id;
            if (id == null) return;
            activeIds.add(id);

            if (timersRef.current.has(id) || exitingRef.current.has(id)) return;

            const remaining = Math.max(
                TOAST_DURATION_MS - (Date.now() - (toast.timestamp ?? Date.now())),
                0
            );
            timersRef.current.set(
                id,
                setTimeout(() => startExit(id), remaining)
            );
        });

        timersRef.current.forEach((_, id) => {
            if (!activeIds.has(id)) clearTimer(id);
        });
    }, [toaster]);

    useEffect(() => () => {
        timersRef.current.forEach(clearTimeout);
        timersRef.current.clear();
    }, []);

    return (
        <div className="notification-container top-right">
            {toaster.map((toast) => {
                const id = toast.id;
                if (id == null) return null;
                const { bgColor, icon } = TOAST_STYLES[toast.type ?? ''] ?? DEFAULT_TOAST_STYLE;
                return (
                    <div
                        key={id}
                        className={`notification toast top-right${exitingIds.has(id) ? ' is-leaving' : ''}`}
                        style={{ backgroundColor: bgColor }}
                    >
                        <button type="button" onClick={() => startExit(id)}>X</button>
                        <div className="d-flex">
                            <div className="notification-image">{icon}</div>
                            <div>
                                <p className="notification-title">{toast.title}</p>
                                <p className="notification-message">{toast.message}</p>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default Alert;
